import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { FileUploadSection } from '../components/FileUploadSection';


export function NewJobOrder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [specialGlassTypes, setSpecialGlassTypes] = useState([]);
  const [windowTypes, setWindowTypes] = useState([]);
  const [executiveNames, setExecutiveNames] = useState([]);
  const [formData, setFormData] = useState({
    jo_number: '',
    jo_date: new Date().toISOString().split('T')[0],
    customer_name: '',
    customer_mobile: '',
    work_location: '',
    quotation_no: '',
    executive_name: '',
    total_sqft: '',
    no_of_windows: '',
    delivery_date: '',
    profile_type: 'White',
    glass_type: 'Normal',
    uploaded_files: []
  });

  const fetchDropdownConfig = useCallback(async () => {
    try {
      const [glassRes, windowRes, execRes] = await Promise.all([
        api.get(`/api/dropdown-config?category=glass_type_special`),
        api.get(`/api/dropdown-config?category=upvc_window_types`),
        api.get(`/api/masters/executives`),
      ]);
      setSpecialGlassTypes(glassRes.data.values || []);
      setWindowTypes(windowRes.data.values || []);
      setExecutiveNames(execRes.data || []);
    } catch {
      toast.error('Failed to load dropdown options');
    }
  }, []);

  useEffect(() => {
    fetchDropdownConfig();
  }, [fetchDropdownConfig]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const fileData = {
        filename: file.name,
        content_type: file.type,
        file_type: type,
        data: reader.result.split(',')[1],
        uploaded_at: new Date().toISOString()
      };
      setFormData(prev => ({
        ...prev,
        uploaded_files: [...prev.uploaded_files, fileData]
      }));
      toast.success(`${type} uploaded successfully`);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post(`/api/job-orders`, formData);
      toast.success('Job Order created successfully');
      navigate('/job-orders');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        if (detail.includes('Duplicate')) {
          toast.error('Duplicate Job Order No already exists. Please use a different Job Order number.');
        } else {
          toast.error(detail);
        }
      } else {
        toast.error('Failed to create job order');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-4xl font-light tracking-tight text-zinc-950">New Job Order</h1>
          <p className="text-sm text-zinc-500 mt-2">Create a new UPVC job order entry</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Details */}
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">Job Order Details</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="jo_number" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Job Order No *</Label>
                    <Input
                      id="jo_number"
                      data-testid="jo-number-input"
                      required
                      value={formData.jo_number}
                      onChange={(e) => handleChange('jo_number', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="jo_date" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">JO Date *</Label>
                    <Input
                      id="jo_date"
                      data-testid="jo-date-input"
                      type="date"
                      required
                      value={formData.jo_date}
                      onChange={(e) => handleChange('jo_date', e.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer Details */}
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">Customer Information</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Customer Name *</Label>
                    <Input
                      id="customer_name"
                      data-testid="customer-name-input"
                      required
                      value={formData.customer_name}
                      onChange={(e) => handleChange('customer_name', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_mobile" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Mobile No *</Label>
                    <Input
                      id="customer_mobile"
                      data-testid="customer-mobile-input"
                      required
                      value={formData.customer_mobile}
                      onChange={(e) => handleChange('customer_mobile', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="work_location" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Work Location *</Label>
                    <Textarea
                      id="work_location"
                      data-testid="work-location-input"
                      required
                      value={formData.work_location}
                      onChange={(e) => handleChange('work_location', e.target.value)}
                      className="min-h-20"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Specifications */}
            <Card className="border-zinc-300 rounded-sm shadow-sm">
              <CardHeader className="border-b border-zinc-300">
                <CardTitle className="text-xl font-bold text-zinc-950">Window Specifications</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="quotation_no" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Quotation No *</Label>
                    <Input
                      id="quotation_no"
                      data-testid="quotation-no-input"
                      required
                      value={formData.quotation_no}
                      onChange={(e) => handleChange('quotation_no', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="executive_name" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Executive Name *</Label>
                    {executiveNames.length > 0 ? (
                      <Select value={formData.executive_name} onValueChange={(val) => handleChange('executive_name', val)}>
                        <SelectTrigger data-testid="executive-name-select" className="h-11">
                          <SelectValue placeholder="Select Executive" />
                        </SelectTrigger>
                        <SelectContent>
                          {executiveNames.map((name) => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="executive_name"
                        data-testid="executive-name-input"
                        required
                        value={formData.executive_name}
                        onChange={(e) => handleChange('executive_name', e.target.value)}
                        className="h-11"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="total_sqft" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft *</Label>
                    <Input
                      id="total_sqft"
                      data-testid="total-sqft-input"
                      type="number"
                      step="0.01"
                      required
                      value={formData.total_sqft}
                      onChange={(e) => handleChange('total_sqft', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="no_of_windows" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">No of Windows *</Label>
                    <Input
                      id="no_of_windows"
                      data-testid="no-of-windows-input"
                      type="number"
                      required
                      value={formData.no_of_windows}
                      onChange={(e) => handleChange('no_of_windows', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_date" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Delivery Date *</Label>
                    <Input
                      id="delivery_date"
                      data-testid="delivery-date-input"
                      type="date"
                      required
                      value={formData.delivery_date}
                      onChange={(e) => handleChange('delivery_date', e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile_type" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Profile Type *</Label>
                    <Select value={formData.profile_type} onValueChange={(val) => handleChange('profile_type', val)}>
                      <SelectTrigger data-testid="profile-type-select" className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="White">White</SelectItem>
                        <SelectItem value="Colour">Colour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="glass_type" className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Glass Type *</Label>
                    <Select value={formData.glass_type} onValueChange={(val) => handleChange('glass_type', val)}>
                      <SelectTrigger data-testid="glass-type-select" className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Normal">Normal</SelectItem>
                        <SelectItem value="Special">Special</SelectItem>
                        {specialGlassTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <FileUploadSection
              uploadedFiles={formData.uploaded_files}
              onFileUpload={handleFileUpload}
            />

            <div className="flex gap-4">
              <Button
                type="submit"
                data-testid="submit-job-order-button"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 px-8"
              >
                {loading ? 'Creating...' : 'Create Job Order'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/job-orders')}
                className="h-11 px-8"
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}
